import { jsPDF } from "jspdf";
import QRCode from "qrcode";

const BRAND = {
  name: "BROTHERS ADVERTISING",
  parent: "A unit of J U ENTERPRISES",
  tagline: "Signage · Branding · Printing",
  phone: "Customer Care: 022 4742 7059  ·  Mobile: +91 80709 70980",
  email: "print@brothersdigital.in",
  gstin: "27APDPH3761H1ZY",
  address: "Shop No. 02, Payal Residency, Plot No. 29, Sector 16, Taloja Phase 2, Navi Mumbai - 410208",
  bank: {
    account_name: "J U ENTERPRISES",
    account_no: "41137233151",
    ifsc: "SBIN0018639",
    bank_name: "State Bank of India",
    branch: "Taloja Panchanand",
    upi: "8070970980-1@okbizaxis",
    gpay: "+91 80709 70980",
  },
};

// Fetch the Brothers Advertising logo once per PDF-generation session and cache the DataURL.
let _logoDataUrl = null;
async function fetchLogoDataUrl() {
  if (_logoDataUrl) return _logoDataUrl;
  try {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/branding/logo.png`;
    const res = await fetch(url);
    if (!res.ok) return null;
    const blob = await res.blob();
    _logoDataUrl = await new Promise((resolve) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.onerror = () => resolve(null);
      r.readAsDataURL(blob);
    });
    return _logoDataUrl;
  } catch { return null; }
}

async function drawHeader(doc, title = "TAX INVOICE") {
  const pageW = doc.internal.pageSize.getWidth();
  doc.setFillColor(0, 0, 0);
  doc.rect(0, 0, pageW, 88, "F");
  // Logo on the left (falls back to plain wordmark if the logo can't be fetched)
  const logoData = await fetchLogoDataUrl();
  let textX = 40;
  if (logoData) {
    try {
      doc.addImage(logoData, "PNG", 40, 14, 60, 60);
      textX = 110;
    } catch { /* ignore — fall back to text-only header */ }
  }
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  doc.text(BRAND.name, textX, 32);
  doc.setFontSize(8);
  doc.setFont("helvetica", "italic");
  doc.setTextColor(255, 180, 100);
  doc.text(BRAND.parent, textX, 46);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(9);
  doc.text(BRAND.tagline, textX, 60);
  doc.text(`${BRAND.phone}  ·  ${BRAND.email}`, textX, 72);
  doc.text(`GSTIN: ${BRAND.gstin}`, textX, 82);
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(22);
  doc.setFont("helvetica", "bold");
  doc.text(title, 40, 123);
}

function drawBankBlock(doc, y) {
  const pageW = doc.internal.pageSize.getWidth();
  doc.setDrawColor(180);
  doc.setLineWidth(0.5);
  doc.setFillColor(248, 248, 248);
  doc.rect(40, y, pageW - 80, 78, "FD");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(0);
  doc.text("BANK DETAILS", 50, y + 14);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  const col2 = pageW / 2 + 20;
  doc.text(`Account Name : ${BRAND.bank.account_name}`, 50, y + 28);
  doc.text(`Account No.  : ${BRAND.bank.account_no}`, 50, y + 40);
  doc.text(`IFSC Code    : ${BRAND.bank.ifsc}`, 50, y + 52);
  doc.text(`Bank         : ${BRAND.bank.bank_name}`, col2, y + 28);
  doc.text(`Branch       : ${BRAND.bank.branch}`, col2, y + 40);
  doc.text(`GSTIN        : ${BRAND.gstin}`, col2, y + 52);
  doc.setFont("helvetica", "bold");
  doc.text(`UPI ID       : ${BRAND.bank.upi}`, 50, y + 66);
  doc.text(`GPay / PhonePe : ${BRAND.bank.gpay}`, col2, y + 66);
  return y + 86;
}

function drawFooter(doc) {
  doc.setFontSize(8);
  doc.setTextColor(120);
  doc.text(BRAND.address, 40, doc.internal.pageSize.getHeight() - 30);
  doc.text(`${BRAND.parent}  ·  Thank you for your business.`, 40, doc.internal.pageSize.getHeight() - 18);
}

async function drawQR(doc, url, x, y, size = 80) {
  const qrData = await QRCode.toDataURL(url, { width: 200, margin: 0 });
  doc.addImage(qrData, "PNG", x, y, size, size);
  doc.setFontSize(7);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(0);
  doc.text("Scan to track", x, y + size + 10);
}

function fmt(n) {
  const v = Math.round(Number(n) || 0);
  return v.toLocaleString("en-IN");
}

function sizeAmount(s) {
  const w = Number(s.width) || 0, h = Number(s.height) || 0;
  const q = Number(s.quantity) || 1, r = Number(s.rate) || 0;
  return Number(s.amount) || ((w > 0 && h > 0) ? w * h * q * r : q * r);
}

function computeItemTotals(it) {
  const gross = (it.sizes || []).reduce((s, sz) => s + sizeAmount(sz), 0)
    + (it.addons || []).reduce((s, a) => s + (Number(a.amount) || (Number(a.quantity || 1) * Number(a.rate || 0))), 0);
  const rate = Number(it.gst_rate) || 0;
  const mode = it.gst_mode || "excluded";
  if (mode === "none" || rate <= 0) return { base: gross, tax: 0, total: gross, gstLabel: "—" };
  if (mode === "included") {
    const base = gross * 100 / (100 + rate);
    return { base, tax: gross - base, total: gross, gstLabel: `${rate}% incl.` };
  }
  const tax = gross * rate / 100;
  return { base: gross, tax, total: gross + tax, gstLabel: `${rate}%` };
}

function computeExtra(charge, rate, mode) {
  const c = Number(charge) || 0;
  const r = Number(rate) || 0;
  if (c <= 0) return { base: 0, tax: 0, total: 0, label: "—" };
  if (mode === "none" || r <= 0) return { base: c, tax: 0, total: c, label: "—" };
  if (mode === "included") {
    const base = c * 100 / (100 + r);
    return { base, tax: c - base, total: c, label: `${r}% incl.` };
  }
  const tax = c * r / 100;
  return { base: c, tax, total: c + tax, label: `${r}%` };
}

function itemsFrom(order) {
  if (Array.isArray(order.items) && order.items.length > 0) return order.items;
  // Legacy single-product wrapping
  return [{
    product_category: order.product_category, product_name: order.product_name,
    material: order.material,
    sizes: (order.width || order.height) ? [{
      width: order.width, height: order.height, unit: order.size_unit || "ft",
      quantity: order.quantity || 1, rate: 0, amount: order.estimated_cost || 0,
    }] : [{ quantity: order.quantity || 1, rate: 0, amount: order.estimated_cost || 0 }],
    addons: [], quantity: order.quantity || 1,
    gst_rate: 0, gst_mode: "none",
  }];
}

async function drawOrderBody(doc, order, payments = [], docNo = "") {
  const pageW = doc.internal.pageSize.getWidth();
  let y = 140;

  // Meta
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  const isQuote = docNo.startsWith("QT");
  doc.text(`${isQuote ? "Quote" : "Invoice"} #: ${docNo}`, 40, y);
  doc.text(`Date: ${new Date(order.created_at || order.quote_date || Date.now()).toLocaleDateString()}`, 40, y + 14);
  doc.text(`Ref: ${order.id}`, 40, y + 28);
  if (order.valid_till) doc.text(`Valid Till: ${order.valid_till}`, 40, y + 42);

  // QR track code renders on invoices only — quotations skip it per admin preference
  if (!isQuote) {
    const trackUrl = `${window.location.origin}/track/${order.id}`;
    await drawQR(doc, trackUrl, pageW - 120, 105);
  }

  y += 70;
  // Billed / Shipping
  doc.setFont("helvetica", "bold");
  doc.text("Billed To:", 40, y);
  doc.setFont("helvetica", "normal");
  doc.text(order.customer_name || "", 40, y + 14);
  if (order.customer_company) doc.text(order.customer_company, 40, y + 28);
  if (order.customer_phone) doc.text(order.customer_phone, 40, y + 42);
  if (order.shipping_address) {
    doc.setFont("helvetica", "bold");
    doc.text("Shipping:", pageW / 2, y);
    doc.setFont("helvetica", "normal");
    const lines = doc.splitTextToSize(order.shipping_address, pageW / 2 - 60);
    doc.text(lines, pageW / 2, y + 14);
  }

  y += 66;
  // Table header
  doc.setDrawColor(180);
  doc.setLineWidth(0.5);
  doc.setFillColor(240, 240, 240);
  doc.rect(40, y - 12, pageW - 80, 20, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  const COL_ITEM = 46, COL_QTY = pageW - 300, COL_RATE = pageW - 235, COL_BASE = pageW - 165, COL_GST = pageW - 100, COL_TOTAL = pageW - 44;
  doc.text("#  Item / Description", COL_ITEM, y);
  doc.text("Qty", COL_QTY, y, { align: "right" });
  doc.text("Rate", COL_RATE, y, { align: "right" });
  doc.text("Base", COL_BASE, y, { align: "right" });
  doc.text("GST", COL_GST, y, { align: "right" });
  doc.text("Total", COL_TOTAL, y, { align: "right" });
  y += 10;
  doc.setDrawColor(200);
  doc.line(40, y, pageW - 40, y);
  y += 14;

  const items = itemsFrom(order);
  let baseAcc = 0, taxAcc = 0;
  const perGstBuckets = {};  // { "18": {base, tax} }

  items.forEach((it, idx) => {
    if (y > doc.internal.pageSize.getHeight() - 160) {
      doc.addPage(); y = 60;
    }
    const t = computeItemTotals(it);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    const heading = `${idx + 1}.  ${it.product_category || ""}${it.product_name ? " — " + it.product_name : ""}`;
    doc.text(heading, COL_ITEM - 6, y);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(90);
    if (it.material || it.printing_type) {
      const meta = [it.material, it.printing_type].filter(Boolean).join(" · ");
      doc.text(meta, COL_ITEM, y + 11);
      y += 8;
    }
    doc.setTextColor(0);
    y += 12;

    (it.sizes || []).forEach((s) => {
      if (y > doc.internal.pageSize.getHeight() - 140) { doc.addPage(); y = 60; }
      const amt = sizeAmount(s);
      const desc = (s.width && s.height)
        ? `   Size ${s.width}×${s.height} ${s.unit || "ft"}`
        : `   Line item`;
      doc.setFontSize(8);
      doc.text(desc, COL_ITEM, y);
      doc.text(String(s.quantity || 1), COL_QTY, y, { align: "right" });
      doc.text(fmt(s.rate), COL_RATE, y, { align: "right" });
      doc.text(fmt(amt), COL_BASE, y, { align: "right" });
      doc.text("", COL_GST, y, { align: "right" });
      doc.text(fmt(amt), COL_TOTAL, y, { align: "right" });
      y += 12;
    });
    (it.addons || []).forEach((a) => {
      if (y > doc.internal.pageSize.getHeight() - 140) { doc.addPage(); y = 60; }
      const amt = Number(a.amount) || (Number(a.quantity || 1) * Number(a.rate || 0));
      doc.setFontSize(8);
      doc.text(`   + ${a.name}`, COL_ITEM, y);
      doc.text(String(a.quantity || 1), COL_QTY, y, { align: "right" });
      doc.text(fmt(a.rate), COL_RATE, y, { align: "right" });
      doc.text(fmt(amt), COL_BASE, y, { align: "right" });
      doc.text("", COL_GST, y, { align: "right" });
      doc.text(fmt(amt), COL_TOTAL, y, { align: "right" });
      y += 12;
    });

    // Per-item GST subtotal row
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.setFillColor(252, 246, 240);
    doc.rect(40, y - 9, pageW - 80, 14, "F");
    doc.text(`   Subtotal (Item ${idx + 1})`, COL_ITEM, y);
    doc.text(fmt(t.base), COL_BASE, y, { align: "right" });
    doc.text(t.gstLabel === "—" ? "—" : `${fmt(t.tax)} (${t.gstLabel})`, COL_GST, y, { align: "right" });
    doc.text(fmt(t.total), COL_TOTAL, y, { align: "right" });
    doc.setFont("helvetica", "normal");
    y += 18;

    baseAcc += t.base;
    taxAcc += t.tax;
    if (t.tax > 0) {
      const key = String(Number(it.gst_rate) || 0);
      const b = perGstBuckets[key] || { base: 0, tax: 0 };
      b.base += t.base; b.tax += t.tax;
      perGstBuckets[key] = b;
    }
  });

  // Extras
  const transport = Number(order.transport_charge) || 0;
  const install = Number(order.installation_charge) || 0;
  const tE = computeExtra(transport, order.transport_gst_rate, order.transport_gst_mode);
  const iE = computeExtra(install, order.installation_gst_rate, order.installation_gst_mode);
  if (tE.total > 0 || iE.total > 0) {
    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.text("Additional Charges", COL_ITEM - 6, y);
    y += 10;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    [["Transport", tE, order.transport_gst_rate], ["Installation", iE, order.installation_gst_rate]].forEach(([name, ex, gr]) => {
      if (ex.total <= 0) return;
      if (y > doc.internal.pageSize.getHeight() - 140) { doc.addPage(); y = 60; }
      doc.text(`   ${name}`, COL_ITEM, y);
      doc.text("1", COL_QTY, y, { align: "right" });
      doc.text(fmt(ex.base), COL_RATE, y, { align: "right" });
      doc.text(fmt(ex.base), COL_BASE, y, { align: "right" });
      doc.text(ex.label === "—" ? "—" : `${fmt(ex.tax)} (${ex.label})`, COL_GST, y, { align: "right" });
      doc.text(fmt(ex.total), COL_TOTAL, y, { align: "right" });
      y += 14;
      baseAcc += ex.base; taxAcc += ex.tax;
      if (ex.tax > 0) {
        const key = String(Number(gr) || 0);
        const b = perGstBuckets[key] || { base: 0, tax: 0 };
        b.base += ex.base; b.tax += ex.tax;
        perGstBuckets[key] = b;
      }
    });
  }

  y += 8;
  doc.setDrawColor(200);
  doc.line(40, y, pageW - 40, y);
  y += 14;

  // GST breakdown box
  if (Object.keys(perGstBuckets).length > 0) {
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.text("GST Breakdown", 40, y);
    doc.setFont("helvetica", "normal");
    y += 12;
    Object.entries(perGstBuckets).sort((a, b) => Number(a[0]) - Number(b[0])).forEach(([rate, b]) => {
      const cgst = b.tax / 2;
      doc.text(`   CGST ${Number(rate) / 2}% on ${fmt(b.base)}`, 46, y);
      doc.text(fmt(cgst), 260, y, { align: "right" });
      doc.text(`   SGST ${Number(rate) / 2}% on ${fmt(b.base)}`, 290, y);
      doc.text(fmt(cgst), pageW / 2 + 130, y, { align: "right" });
      y += 12;
    });
    y += 6;
  }

  // Totals block
  const discount = Number(order.discount_amount) || 0;
  const grand = Math.max(baseAcc + taxAcc - discount, 0);
  const rows = [
    ["Subtotal (before GST)", baseAcc, false],
    ...(taxAcc > 0 ? [["Total GST", taxAcc, false]] : []),
    ...(discount > 0 ? [["Discount", -discount, false]] : []),
    ["Grand Total", grand, true],
  ];
  rows.forEach(([lbl, amt, bold]) => {
    if (y > doc.internal.pageSize.getHeight() - 100) { doc.addPage(); y = 60; }
    doc.setFont("helvetica", bold ? "bold" : "normal");
    doc.setFontSize(bold ? 11 : 9);
    doc.text(lbl, pageW - 200, y);
    doc.text(`Rs. ${fmt(amt)}`, pageW - 40, y, { align: "right" });
    y += bold ? 18 : 14;
  });

  if (payments.length) {
    y += 8;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.text("Payments Received", 40, y);
    y += 14;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    payments.forEach((p) => {
      if (y > doc.internal.pageSize.getHeight() - 60) { doc.addPage(); y = 60; }
      doc.text(`${new Date(p.created_at).toLocaleDateString()}  ·  ${String(p.mode || "").toUpperCase()}  ·  Rs. ${fmt(p.amount)}  ·  ${p.note || ""}`, 40, y);
      y += 12;
    });
    const paid = payments.reduce((s, p) => s + Number(p.amount || 0), 0);
    y += 4;
    doc.setFont("helvetica", "bold");
    doc.text(`Paid: Rs. ${fmt(paid)}    Balance: Rs. ${fmt(grand - paid)}`, 40, y);
    y += 14;
  }
  return y;
}

export async function downloadInvoicePDF({ order, payments = [] }) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  await drawHeader(doc, "TAX INVOICE");
  let y = await drawOrderBody(doc, order, payments, `INV-${order.id}`);
  // Reserve room for bank block + footer; new page if not enough
  const pageH = doc.internal.pageSize.getHeight();
  if (y > pageH - 150) { doc.addPage(); y = 60; }
  drawBankBlock(doc, Math.max(y + 20, pageH - 150));
  drawFooter(doc);
  doc.save(`Invoice-${order.id}.pdf`);
}

export async function downloadQuotationPDF({ quotation }) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  await drawHeader(doc, "QUOTATION");
  const shim = { ...quotation, created_at: quotation.quote_date || quotation.created_at };
  let y = await drawOrderBody(doc, shim, [], quotation.id);
  const pageH = doc.internal.pageSize.getHeight();
  if (y > pageH - 150) { doc.addPage(); y = 60; }
  drawBankBlock(doc, Math.max(y + 20, pageH - 150));
  drawFooter(doc);
  doc.save(`Quotation-${quotation.id}.pdf`);
}

export async function downloadCustomerStatementPDF({ customer, orders, payments, totals, dateFrom, dateTo }) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  await drawHeader(doc, "CUSTOMER STATEMENT");
  const pageW = doc.internal.pageSize.getWidth();
  let y = 140;

  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.text(customer.name, 40, y);
  doc.setFont("helvetica", "normal");
  if (customer.company) doc.text(customer.company, 40, y + 14);
  if (customer.phone) doc.text(customer.phone, 40, y + 28);
  if (customer.gst) doc.text(`GSTIN: ${customer.gst}`, 40, y + 42);
  const period = `${dateFrom || "Beginning"} → ${dateTo || "Today"}`;
  doc.text(`Period: ${period}`, pageW - 250, y);
  doc.text(`Statement generated: ${new Date().toLocaleDateString()}`, pageW - 250, y + 14);

  y += 66;
  doc.setFillColor(240, 240, 240);
  doc.rect(40, y - 12, pageW - 80, 20, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.text("Date", 46, y);
  doc.text("Description", 130, y);
  doc.text("Debit (₹)", pageW - 200, y, { align: "right" });
  doc.text("Credit (₹)", pageW - 110, y, { align: "right" });
  doc.text("Balance (₹)", pageW - 44, y, { align: "right" });
  y += 10;
  doc.setDrawColor(200); doc.line(40, y, pageW - 40, y); y += 14;

  // Interleave orders (debits) and payments (credits) by date
  const entries = [];
  orders.forEach((o) => entries.push({
    date: o.created_at || "", type: "order", label: `Order ${o.id}${o.product_name ? " — " + o.product_name : ""}`,
    debit: Number(o.estimated_cost) || 0, credit: 0,
  }));
  payments.forEach((p) => entries.push({
    date: p.created_at || "", type: "payment", label: `Payment ${p.mode ? "(" + p.mode.toUpperCase() + ")" : ""} — Order ${p.order_id || ""}`,
    debit: 0, credit: Number(p.amount) || 0,
  }));
  entries.sort((a, b) => (a.date < b.date ? -1 : 1));

  let running = 0;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  entries.forEach((e) => {
    if (y > doc.internal.pageSize.getHeight() - 120) { doc.addPage(); y = 60; }
    running += e.debit - e.credit;
    const dateStr = e.date ? new Date(e.date).toLocaleDateString() : "—";
    doc.text(dateStr, 46, y);
    const lines = doc.splitTextToSize(e.label, pageW - 380);
    doc.text(lines, 130, y);
    doc.text(e.debit > 0 ? fmt(e.debit) : "—", pageW - 200, y, { align: "right" });
    doc.text(e.credit > 0 ? fmt(e.credit) : "—", pageW - 110, y, { align: "right" });
    doc.text(fmt(running), pageW - 44, y, { align: "right" });
    y += Math.max(14, lines.length * 10);
  });
  if (entries.length === 0) {
    doc.setTextColor(120);
    doc.text("No transactions in this period.", 46, y);
    doc.setTextColor(0);
    y += 20;
  }

  y += 10;
  doc.setDrawColor(120); doc.line(40, y, pageW - 40, y); y += 16;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text("Total Billed", pageW - 200, y); doc.text(`Rs. ${fmt(totals.billed)}`, pageW - 44, y, { align: "right" }); y += 14;
  doc.text("Total Paid", pageW - 200, y); doc.text(`Rs. ${fmt(totals.paid)}`, pageW - 44, y, { align: "right" }); y += 14;
  doc.setTextColor(255, 107, 0);
  doc.setFontSize(12);
  doc.text("Outstanding", pageW - 200, y); doc.text(`Rs. ${fmt(totals.outstanding)}`, pageW - 44, y, { align: "right" });
  doc.setTextColor(0);

  y += 26;
  const pageH = doc.internal.pageSize.getHeight();
  if (y > pageH - 150) { doc.addPage(); y = 60; }
  drawBankBlock(doc, Math.max(y, pageH - 150));
  drawFooter(doc);
  const fromStr = (dateFrom || "start").replace(/[^0-9]/g, "") || "start";
  const toStr = (dateTo || "today").replace(/[^0-9]/g, "") || "today";
  doc.save(`Statement-${customer.name.replace(/\s+/g, "_")}-${fromStr}-${toStr}.pdf`);
}
